import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ReferralFeedbackProviderDetailPanel } from './ReferralFeedbackProviderDetailPanel';
import type { ReferralFeedbackSpSummaryRow } from '../../../types/engagement-metrics.types';

const reachRate = (row: ReferralFeedbackSpSummaryRow): string =>
  row.followupsSent > 0
    ? `${Math.round((row.reachedCount / row.followupsSent) * 100)}%`
    : '—';

const rating = (value: number | null): string => (value != null ? value.toFixed(1) : '—');

const HEADERS = [
  'Provider',
  'Follow-ups sent',
  'Reach rate',
  'Conversions',
  'Not yet',
  'Avg SP rating',
  'Avg CS rating',
  'Withheld',
  '', // chevron column
];

/**
 * Per-SP follow-up summary with clickable rows: clicking a provider expands an
 * inline detail row (single-open accordion) that lazy-loads that SP's
 * per-referral drill-down.
 */
export const ReferralFeedbackSpTable = ({ rows }: { rows: ReferralFeedbackSpSummaryRow[] }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = (spUserId: string) =>
    setExpandedId((current) => (current === spUserId ? null : spUserId));

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr className="border-b" style={{ borderColor: '#edeae3' }}>
            {HEADERS.map((header, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.3px]"
                style={{ color: '#7f8a83' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedId === row.spUserId;
            return [
              <tr
                key={row.spUserId}
                className={`cursor-pointer transition-colors hover:bg-[#faf8f4] ${
                  isExpanded ? 'bg-[#faf8f4]' : 'border-b last:border-0'
                }`}
                style={{ borderColor: '#edeae3' }}
                onClick={() => toggle(row.spUserId)}
                data-testid={`referral-sp-row-${row.spUserId}`}
              >
                <td className="px-3 py-2.5">
                  <p className="text-[13px] font-semibold" style={{ color: '#1b2a23' }}>
                    {row.spName}
                  </p>
                  <p className="text-[11px]" style={{ color: '#7f8a83' }}>
                    {row.companyName ?? 'Independent provider'}
                  </p>
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {row.followupsSent}
                </td>
                <td className="px-3 py-2.5 text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
                  {reachRate(row)}
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {row.conversions}
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {row.notYet}
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {rating(row.avgSpRating)}
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {rating(row.avgCsRating)}
                </td>
                <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                  {row.withheldCount}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} referrals for ${row.spName}`}
                    className="rounded p-1 hover:bg-[#edeae3]"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(row.spUserId);
                    }}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      style={{ color: '#7f8a83' }}
                    />
                  </button>
                </td>
              </tr>,
              isExpanded && (
                <tr
                  key={`${row.spUserId}-detail`}
                  className="border-b last:border-0"
                  style={{ borderColor: '#edeae3', backgroundColor: '#faf8f4' }}
                >
                  <td colSpan={HEADERS.length} className="px-4 pb-3">
                    <ReferralFeedbackProviderDetailPanel spUserId={row.spUserId} />
                  </td>
                </tr>
              ),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
};
